import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { catchError, map, Observable, of } from 'rxjs';
import { SelectItem } from '@web/shared/ui';
import {
  ShippingMethodProperty,
  ShippingMethodPropertyMetadata,
} from '@web/shared/module/shop';
import { AdminOrderShippingMethodCollectionItemResponse } from '../api/responses/admin-order-shipping-method-collection-item.response';
import { AdminOrderApiService } from '../api/services/admin-order-api.service';
import { AdminOrderShippingMethodCollectionItem } from '../models/admin-order-shipping-method-collection-item';

@Injectable({
  providedIn: 'root',
})
export class AdminOrderShippingMethodSelectItemsProviderService {
  private readonly orderApi = inject(AdminOrderApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  public fetchSelectItems<
    Key extends keyof AdminOrderShippingMethodCollectionItemResponse,
  >(params: {
    countryId?: Nillable<Guid>;
    key: Key;
    orderId: Guid;
    query?: Nillable<string>;
    shipmentId: Guid;
  }): Observable<
    SelectItem<AdminOrderShippingMethodCollectionItemResponse[Key]>[]
  > {
    const { orderId, key, query, countryId, shipmentId } = params;

    /* prettier-ignore */
    return this.orderApi
      .fetchAvailableShippingMethodCollection({
        request: this.collectionRequestBuilder
          .disablePagination()
          .build(),
        countryId,
        shipmentId,
        orderId,
        skipByLoading: true,
        cacheable: false,
      })
      .pipe(
        map((response) =>
          response.items
            .filter((item) => {
              if (isNil(query)) {
                return item;
              }

              return item.name.toLowerCase().includes(query);
            })
            .map((item) => new SelectItem(item.name, item[key])),
        ),
        catchError(() => of([])),
      );
  }

  public fetchShippingMethodById(params: {
    id: Guid;
    countryId?: Nillable<Guid>;
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<Nillable<AdminOrderShippingMethodCollectionItem>> {
    const { orderId, id, countryId, shipmentId } = params;

    return this.orderApi
      .fetchAvailableShippingMethodCollection({
        request: this.collectionRequestBuilder.disablePagination().build(),
        countryId,
        shipmentId,
        orderId,
        skipByLoading: true,
        cacheable: false,
      })
      .pipe(
        map((response) =>
          response.items
            .filter((item) => item.id === id)
            .map(
              (item) =>
                new AdminOrderShippingMethodCollectionItem({
                  code: item.code,
                  name: item.name,
                  id: item.id,
                  description: item.description,
                  shippingPrice: item.shippingPrice,
                  totalPrice: item.totalPrice,
                  properties: item.properties.map(
                    (property) =>
                      new ShippingMethodProperty({
                        code: property.code,
                        name: property.name,
                        value: property.value,
                        metadata: valueOrDefault(property.metadata, []).map(
                          (metadata) =>
                            new ShippingMethodPropertyMetadata({
                              code: metadata.code,
                              value: metadata.value,
                            }),
                        ),
                      }),
                  ),
                }),
            ),
        ),
        map((items) => {
          const [first] = items;

          return first;
        }),
        catchError(() => of(null)),
      );
  }
}
